import { useCallback, useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'

export type LegalPageKey = 'mentions-legales' | 'politique-confidentialite' | 'cgv'

export interface LegalSection {
  title: string
  content: string // HTML enrichi
}

export interface LegalPageDoc {
  _id: string
  pageKey: LegalPageKey
  title: string
  h1: string
  metaDescription: string
  sections: LegalSection[]
  creator: string
  createdAt: string
  updatedAt: string
}

export interface LegalPageInput {
  pageKey: LegalPageKey
  title: string
  h1: string
  metaDescription: string
  sections: LegalSection[]
}

export function useLegalPages() {
  const [docs, setDocs] = useState<LegalPageDoc[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await lumi.entities.legal_pages.list({ sort: { updatedAt: -1 } })
      const list = (res.list as LegalPageDoc[]) || []
      setDocs(list)
      console.log('[useLegalPages] fetched:', list.length)
    } catch (e) {
      const msg = (e as Error)?.message || 'Erreur de chargement des pages légales'
      console.error('[useLegalPages] fetch failed:', e)
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  /**
   * Upsert atomique par pageKey : on prend le premier doc trouvé pour cette clé,
   * on met à jour, sinon on crée. Si plusieurs docs existent pour la même clé,
   * on garde le plus récent et on supprime les autres pour éviter les conflits.
   */
  const savePage = useCallback(
    async (input: LegalPageInput, creator?: string) => {
      const now = new Date().toISOString()
      const sameKey = docs
        .filter((d) => d.pageKey === input.pageKey)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())

      if (sameKey.length > 0) {
        const target = sameKey[0]
        const updated = await lumi.entities.legal_pages.update(target._id, {
          title: input.title,
          h1: input.h1,
          metaDescription: input.metaDescription,
          sections: input.sections,
          updatedAt: now,
        })
        console.log('[useLegalPages] updated:', target._id, input.pageKey)

        // Nettoyage des doublons éventuels
        for (const stale of sameKey.slice(1)) {
          try {
            await lumi.entities.legal_pages.delete(stale._id)
            console.log('[useLegalPages] cleaned duplicate:', stale._id)
          } catch (err) {
            console.warn('[useLegalPages] cleanup duplicate failed (non bloquant):', err)
          }
        }
        await fetchAll()
        return updated
      }

      const created = await lumi.entities.legal_pages.create({
        pageKey: input.pageKey,
        title: input.title,
        h1: input.h1,
        metaDescription: input.metaDescription,
        sections: input.sections,
        creator: creator ?? 'admin',
        createdAt: now,
        updatedAt: now,
      })
      console.log('[useLegalPages] created:', created?._id, input.pageKey)
      await fetchAll()
      return created
    },
    [docs, fetchAll],
  )

  const getByKey = useCallback(
    (key: LegalPageKey): LegalPageDoc | undefined => {
      const sameKey = docs
        .filter((d) => d.pageKey === key)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      return sameKey[0]
    },
    [docs],
  )

  return { docs, loading, error, fetchAll, savePage, getByKey }
}
